import { extend, isEqualString, unwrapBox, wrapBox } from "@dhx/ts-common/core";
import { awaitRedraw, create, el } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { getLabelStyle, locate } from "@dhx/ts-common/html";
import { KEY_CODES } from "@dhx/ts-common/keycodes";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents, IDataEventsHandlersMap } from "@dhx/ts-data";
import { Layout } from "@dhx/ts-layout";
import { List, ListEvents, MOVE_DOWN, MOVE_UP } from "@dhx/ts-list";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { KeyListener } from "./keyListener";
import locale from "./locales/en";

import { Id, IHandlers, SelectionEvents } from "@dhx/ts-common/types";
import { emptyListHeight, emptyListView, selectAllView, unselectAllView } from "./helper";
import {
	ComboboxEvents,
	ComboState,
	ICombobox,
	IComboboxConfig,
	IComboboxEventHandlersMap,
	State,
} from "./types";

type TemplateFn = (count: number) => string;
function itemsCountTemplate(count, templateFN: boolean | TemplateFn) {
	if (typeof templateFN === "function") {
		return templateFN(count);
	} else {
		return count + " " + locale.selectedItems;
	}
}

const template = item => {
	if (item.icon) {
		return `<span class="${item.icon} dhx_combobox-options__icon"></span> <span class="dhx_combobox-options__value">${item.value}</span>`;
	}
	if (item.src) {
		return `<img src="${item.src}" class="dhx_combobox-options__image" alt=${item.value}></img> <span class="dhx_combobox-options__value">${item.value}</span>`;
	}
	return `<span class="dhx_combobox-options__value">${item.value}</span>`;
};

export class Combobox extends View implements ICombobox {
	public data: DataCollection;
	public config: IComboboxConfig;
	public events: IEventSystem<
		DataEvents | ComboboxEvents,
		IComboboxEventHandlersMap | IDataEventsHandlersMap
	>;
	public list: List;
	public popup: Popup;

	protected _helper: Popup;

	private _keyListener: KeyListener;
	protected _layout: Layout;
	private _isPopupConfiqureted: boolean;

	private _state: {
		value: string;
		ignoreNext: boolean;
		canDelete: boolean;
		unselectActive: boolean;
		currentState: ComboState;
		creatingState: boolean;
	};

	protected _handlers: IHandlers;

	constructor(element: HTMLElement | string, config: IComboboxConfig) {
		super(
			element,
			extend(
				{
					template,
					listHeight: 224,
					itemHeight: 36,
					disabled: false,
					readOnly: false,
					newOptions: false,
					htmlEnable: true,
				},
				config
			)
		);
		this.config.itemsCount = this.config.itemsCount || this.config.showItemsCount; // TODO: remove suite_7.0
		this.config.helpMessage = this.config.helpMessage || this.config.help; // TODO: remove suite_7.0
		if (this.config.cellHeight && this.config.itemHeight === 36) {
			this.config.itemHeight = this.config.cellHeight; // TODO: remove suite_7.0
		}
		if (this.config.labelInline) {
			this.config.labelPosition = "left"; // TODO: remove suite_7.0
		}
		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<ComboboxEvents>(this);
			this.data = new DataCollection({});
			this.data.parse(this.config.data);
		} else if (this.config.data) {
			this.data = this.config.data;
			this.events = new EventSystem<ComboboxEvents>(this);
			this.events.context = this;
		} else {
			this.events = new EventSystem<ComboboxEvents>(this);
			this.data = new DataCollection({});
		}

		this.popup = new Popup();
		this.popup.events.on(PopupEvents.afterShow, () => {
			this.paint();
		});
		this.popup.events.on(PopupEvents.afterHide, () => {
			if (this.config.multiselection) {
				this._state.value = "";
			}
			this.paint();
		});
		this.popup.events.on(PopupEvents.beforeHide, fromOuterClick => {
			fromOuterClick && this._hideOptions();
		});
		if (this.config.readonly || this.config.readOnly) {
			this.config.readOnly = this.config.readOnly || this.config.readonly;
			this._keyListener = new KeyListener();
		}
		this._state = {
			value: "",
			ignoreNext: false,
			canDelete: false,
			unselectActive: false,
			currentState: ComboState.default,
			creatingState: false,
		};

		this._initHandlers();
		this._createLayout();

		if (this.config.value) {
			this._setValue(this.config.value, true);
		}

		this._initEvents();

		const vnode = create({
			render: () => this._draw(),
			hooks: {
				didRedraw: () => {
					if (this.popup.isVisible()) {
						this.focus();
						this._configurePopup();
					}
				},
			},
		});
		// const container = toNode(element);
		this.mount(element, vnode);
	}
	focus(): void | boolean {
		if (this.config.disabled) return false;

		const rootView = this.getRootView();
		rootView.refs.input.el.focus();
	}
	blur(): void | boolean {
		if (this.config.disabled) return false;

		const rootView = this.getRootView();
		rootView.refs.input.el.blur();
		this.popup.hide();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	clear(): void | boolean {
		this.list.selection.remove();
		this._state.value = "";
		this._filter();
		this.paint();
	}
	getValue<T extends boolean = false>(asArray?: T): T extends true ? Id[] : Id {
		const ids = this.list.selection.getId();
		if (asArray) {
			return wrapBox(ids) as any;
		}
		return Array.isArray(ids) ? ids.join(",") : (ids as any);
	}
	setValue(ids: Id[] | Id): void | boolean {
		return this._setValue(ids, false);
	}
	addOption(value: string, join = true): void {
		if (!value || typeof value !== "string") {
			return;
		}
		const id = this.data.add({ value });
		const options =
			this.config.multiselection && join ? [...(this.list.selection.getId() as Id[]), id] : id;
		this.setValue(options);
	}
	destructor(): void {
		this.popup && this.popup.destructor();
		this.events && this.events.clear();
		this.list && this.list.destructor();
		this._helper && this._helper.destructor();
		this._layout && this._layout.destructor();

		this.config = this.events = this.list = this.popup = null;
		this._helper = this._keyListener = this._handlers = this._state = this._uid = this._isPopupConfiqureted = null;
		this.unmount();
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	setState(state: State) {
		switch (state) {
			case "success":
				this._state.currentState = ComboState.success;
				break;
			case "error":
				this._state.currentState = ComboState.error;
				break;
			default:
				this._state.currentState = ComboState.default;
				break;
		}
		this.paint();
	}
	private _setValue(ids: Id | Id[], silent = false): boolean | void {
		if (!this._exsistId(ids)) {
			return false;
		}
		this._filter();

		this.list.selection.remove();

		if (this.config.multiselection) {
			if (typeof ids === "string") {
				ids = ids.split(",");
			}
			if (typeof ids === "number") {
				ids = [ids];
			}
			ids.forEach(id => {
				this.list.selection.add(id, false, false, silent);
			});
		} else {
			const id = unwrapBox(ids);
			this.list.selection.add(id, false, false, silent);
			const item = this.data.getItem(id);
			if (item) {
				this._state.value = this._getItemText(item);
			}
		}
		this.paint();
	}
	protected _createLayout() {
		const list = (this.list = new List(null, {
			template: this.config.template,
			htmlEnable: this.config.htmlEnable,
			virtual: this.config.virtual,
			keyNavigation: false,
			multiselection: this.config.multiselection,
			itemHeight: this.config.itemHeight,
			height: this.config.listHeight,
			data: this.data,
		}));

		const layout = (this._layout = new Layout(this.popup.getContainer(), {
			css: "dhx_combobox-options dhx_combobox__options",
			rows: [
				{
					id: "select-unselect-all",
					hidden: !this.config.multiselection || !this.config.selectAllButton,
				},
				{ id: "list", height: "content" },
				{
					id: "not-found",
					hidden: true,
				},
			],
			on: {
				click: {
					".dhx_combobox__action-select-all": this._handlers.selectAll,
					".dhx_combobox-options__action-create-option": this._handlers.addOption,
				},
			},
		}));

		layout.getCell("list").attach(list);
		if (this.config.multiselection && this.config.selectAllButton) {
			layout.getCell("select-unselect-all").attach(selectAllView);
		}
	}
	private _initHandlers() {
		if (this.config.helpMessage) {
			this._helper = new Popup({
				css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light",
			});
			this._helper.attachHTML(this.config.helpMessage);
		}
		this._handlers = {
			addOption: () => this.addOption(this._state.value),
			showHelper: e => {
				e.preventDefault();
				e.stopPropagation();
				this._helper.show(e.target, {
					mode: this.config.labelPosition === "left" ? "bottom" : "right",
				});
			},
			selectAll: () => {
				if (this._state.unselectActive) {
					this.list.selection.remove();
					if (this.config.selectAllButton) {
						this._layout.getCell("select-unselect-all").attach(selectAllView);
						this._state.unselectActive = false;
					}
				} else {
					this.data.filter();
					this.list.selection.add();
					if (this.config.selectAllButton) {
						this._layout.getCell("select-unselect-all").attach(unselectAllView);
						this._state.unselectActive = true;
					}
				}
				this._changePopupPosition();
				this.paint();
			},
			onkeydown: (e: KeyboardEvent) => {
				if (e.key === "Tab" && this.popup.isVisible()) {
					this._hideOptions();
				} else {
					if (!this.popup.isVisible() && e.which === KEY_CODES.DOWN_ARROW) {
						this._showOptions();
					}
					if (this.popup.isVisible()) {
						if (e.which === KEY_CODES.RIGHT_ARROW) {
							if (this.config.readOnly && !this.config.multiselection) {
								this.list.moveFocus(MOVE_DOWN);
								e.preventDefault();
							}
						}
						if (e.which === KEY_CODES.LEFT_ARROW) {
							if (this.config.readOnly && !this.config.multiselection) {
								this.list.moveFocus(MOVE_UP);
								e.preventDefault();
							}
						}
						if (e.which === KEY_CODES.DOWN_ARROW) {
							this.list.moveFocus(MOVE_DOWN);
							e.preventDefault();
						}
						if (e.which === KEY_CODES.UP_ARROW) {
							this.list.moveFocus(MOVE_UP);
							e.preventDefault();
						}
						if (e.which === KEY_CODES.ESC) {
							this._hideOptions();
						}
						if (e.which === KEY_CODES.ENTER) {
							if (this.config.newOptions && !this.data.getLength()) {
								this.addOption(this._state.value, true);
							} else {
								const id = this.list.getFocus();
								const value = this.config.multiselection
									? [...(this.list.selection.getId() as Id[]), id]
									: id;
								this.setValue(value);
							}
							if (!this.config.multiselection) {
								this._hideOptions();
							} else {
								this._updatePopup();
							}
						}
					}
				}
				this.events.fire(ComboboxEvents.keydown, [e, this.list.getFocus()]);
			},
			onkeyup: (e: KeyboardEvent) => {
				if (!this.config.multiselection || this.config.itemsCount) {
					return;
				}
				if (this._state.ignoreNext) {
					this._state.ignoreNext = false;
					return;
				}
				if (
					e.which === KEY_CODES.BACKSPACE &&
					!this._state.value &&
					this.config.multiselection &&
					(this.list.selection.getId() as string[]).length
				) {
					const selected = this.list.selection.getId() as string[];
					const id = selected[selected.length - 1];
					this.list.selection.remove(id);
					this._changePopupPosition();

					this.paint();
				}
			},
			oninput: (e: Event) => {
				if (this.config.disabled) {
					return;
				}
				const input = e.target as HTMLInputElement;
				const value = input.value;
				this.events.fire(ComboboxEvents.input, [value]);

				this._state.value = value;
				this._filter();

				if (!value.length) {
					this._state.ignoreNext = true;
					this._state.canDelete = true;
				} else {
					this._state.canDelete = false;
				}

				if (!this.config.multiselection) {
					this.list.selection.remove();
					this.paint();
				}

				if (!this.popup.isVisible()) {
					this._showOptions();
				}
				this._updatePopup();
			},
			oninputclick: (e: MouseEvent) => {
				if (this.config.disabled) {
					return;
				}
				this.focus();
				if ((e.target as HTMLElement).classList.contains("dhx_combobox__action-remove")) {
					const id: Id = this.data.getItem(locate(e))?.id as Id;
					if (!id) {
						return;
					}
					this.list.selection.remove(id);
					this._changePopupPosition();
					this.paint();
					return;
				}
				if ((e.target as HTMLElement).classList.contains("dhx_combobox__action-clear-all")) {
					(this.list.selection.getId() as string[]).forEach(id => this.list.selection.remove(id));
					if (this.config.selectAllButton && this._state.unselectActive) {
						this._layout.getCell("select-unselect-all").attach(selectAllView);
						this._state.unselectActive = false;
					}
					this.paint();
					return;
				}
				e.preventDefault();
				if (!this.popup.isVisible()) {
					this._showOptions();
					return;
				}
				this.focus();
			},
			toggleIcon: () => {
				this.focus();
				if (this.popup.isVisible()) {
					this._hideOptions();
				} else {
					this._showOptions();
				}
			},
			onfocus: () => this.events?.fire(ComboboxEvents.focus, []),
			onblur: () => this.events?.fire(ComboboxEvents.blur, []),
		};
	}
	private _initEvents() {
		this.data.events.on(DataEvents.load, () => {
			if (this.config.value) {
				this._setValue(this.config.value, true);
			}
		});
		this.data.events.on(DataEvents.afterAdd, () => {
			if (!this.config.multiselection) {
				this._hideOptions();
			} else {
				this._changePopupPosition();
			}
		});
		this.list.events.on(ListEvents.click, () => {
			if (!this.config.multiselection) {
				this._hideOptions();
			}
			this._changePopupPosition();
		});
		this.list.selection.events.on(SelectionEvents.afterSelect, () => {
			const value = this.getValue(this.config.multiselection);
			this.events.fire(ComboboxEvents.change, [value]);
			this._updateSelectedItem(value);
		});
		this.list.selection.events.on(SelectionEvents.afterUnSelect, () => {
			const multi = this.config.multiselection;
			if (this.config.readOnly && !multi) return;

			const value = this.getValue(multi);
			this.events.fire(ComboboxEvents.change, [value]);
			if (multi) this._updateSelectedItem(value);
		});
		this.popup.events.on(PopupEvents.beforeShow, () => {
			if (!this.popup.isVisible() && !this._isPopupConfiqureted) {
				this._configurePopup();
				return false;
			}
		});
		if (this.config.readOnly) {
			this.popup.events.on(PopupEvents.afterShow, () => {
				if (this._state.value) {
					const id = this.list.selection.getId();
					this.list.setFocus(id as string);
				} else {
					this.list.setFocus(this.data.getId(0));
				}
				this._keyListener.startNewListen(val => this._findBest(val));
			});
		}
	}
	private _showOptions() {
		if (!this.events.fire(ComboboxEvents.beforeOpen)) {
			return;
		}
		if (this._state.value.length) {
			this._state.canDelete = true;
		}
		this._filter();
		if (this._configurePopup()) {
			this.events.fire(ComboboxEvents.open);
			this.events.fire(ComboboxEvents.afterOpen);
		}
	}
	private _configurePopup() {
		this._isPopupConfiqureted = true;
		const rootView = this.getRootView();
		if (!rootView || !rootView.refs || !rootView.refs.holder) {
			return false;
		}
		if (!this.popup.isVisible()) {
			this._updatePopup();
		}
		return true;
	}
	private _hideOptions() {
		if (!this.events.fire(ComboboxEvents.beforeClose)) {
			return;
		}
		if (this.config.readOnly) {
			this._keyListener.endListen();
		}
		this.list.setFocus(this.data.getId(0));
		if (!this.config.multiselection && !this.config.readOnly && !this.list.selection.contains()) {
			this._state.value = "";
		}
		awaitRedraw().then(() => this.popup.isVisible() && this.popup.hide());
		this.events.fire(ComboboxEvents.afterClose);
		this.events.fire(ComboboxEvents.close); // TODO: remove suite_7.0
		this._filter();
		this.paint();
	}
	private _filter() {
		if (this.config.readOnly) {
			return;
		}
		this.data.filter(item =>
			this.config.filter
				? this.config.filter(item, this._state.value)
				: isEqualString(this._state.value, this._getItemText(item))
		);
		if (this.config.multiselection) {
			this.list.setFocus(this.data.getId(0));
		} else {
			const index = this.data.getIndex(this.list.selection.getId() as Id);
			this.list.setFocus(this.data.getId(index > -1 ? index : 0));
		}
		const listCell = this._layout.getCell("list");
		const notFoundCell = this._layout.getCell("not-found");
		if (this.data.getLength() === 0) {
			if (this.config.multiselection && this.config.selectAllButton) {
				this._layout.getCell("select-unselect-all").hide();
			}
			listCell.hide();
			this._state.creatingState = true;
			const notFoundContent = this.config.newOptions ? this._state.value : "";
			notFoundCell.height = emptyListHeight(
				notFoundContent,
				this.getRootView().refs.holder.el.offsetWidth
			);
			notFoundCell.attach(emptyListView, notFoundContent);
			notFoundCell.show();
		} else {
			if (this.config.multiselection && this.config.selectAllButton) {
				this._layout.getCell("select-unselect-all").show();
			}
			const sameItem = this._state.value && this.data.find(item => item.value === this._state.value);
			if (!sameItem && this._state.value) {
				this._state.creatingState = this.config.newOptions;
				listCell.show();
				if (this.config.newOptions) {
					const notFoundContent = this._state.value;
					notFoundCell.height = emptyListHeight(
						notFoundContent,
						this.getRootView().refs.holder.el.offsetWidth
					);
					notFoundCell.attach(emptyListView, notFoundContent);
					notFoundCell.show();
				} else notFoundCell.hide();
			} else {
				this._state.creatingState = false;
				if (notFoundCell.isVisible()) {
					listCell.show();
					notFoundCell.hide();
				}
			}
		}
	}
	private _findBest(value: string) {
		const best = this.data.find(item => isEqualString(value, this._getItemText(item)));
		if (!best) {
			return;
		}

		if (this.list.selection.getId() === best.id) {
			return;
		}

		this.list.setFocus(best.id);
		this.paint();
	}
	private _exsistId(id: Id | Id[]) {
		if (id instanceof Array) {
			return id.every(i => this.data.exists(i));
		}
		return this.data.exists(id);
	}
	private _draw() {
		if (!this.config) return el("div");
		const {
			multiselection,
			labelPosition,
			hiddenLabel,
			required,
			disabled,
			css,
			helpMessage,
			readOnly,
			placeholder,
		} = this.config;
		const item = multiselection ? null : this.data.getItem(this.list.selection.getId() as Id);
		const showPlaceholder =
			!this.list.selection.getId() ||
			(typeof this.list.selection.getId() === "object" &&
				(this.list.selection.getId() as Id[]).length === 0);
		const labelStyle = getLabelStyle(this.config);
		return el(
			"div",
			{
				"data-dhx-widget-id": this._uid,
				onkeydown: this._handlers.onkeydown,
				onkeyup: this._handlers.onkeyup,
				class:
					"dhx_widget dhx_combobox" +
					(labelPosition === "left" ? " dhx_combobox--label-inline" : "") +
					(hiddenLabel ? " dhx_combobox--sr_only" : "") +
					(required ? " dhx_combobox--required" : "") +
					(disabled ? " dhx_combobox--disabled" : "") +
					(css ? ` ${css}` : ""),
			},
			[
				labelStyle
					? el(
							"label.dhx_label.dhx_combobox__label",
							{
								style: labelStyle.style,
								class: helpMessage ? "dhx_label--with-help" : "",
								onclick: this._handlers.oninputclick,
							},
							helpMessage
								? [
										(labelStyle.label || required) &&
											el("span.dhx_label__holder", labelStyle.label),
										el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
											tabindex: "0",
											role: "button",
											onclick: this._handlers.showHelper,
											id: `dhx_label__help_${this._uid}`,
										}),
								  ]
								: labelStyle.label
					  )
					: null,
				el(
					"div.dhx_combobox-input-box" +
						// (this.popup.isVisible() ? ".dhx_combobox-input-box" : "") +
						(disabled ? ".dhx_combobox-input-box--disabled" : "") +
						(readOnly ? ".dhx_combobox-input-box--readonly" : "") +
						(this._state.currentState === ComboState.error
							? ".dhx_combobox-input-box--state_error"
							: "") +
						(this._state.currentState === ComboState.success
							? ".dhx_combobox-input-box--state_success"
							: ""),
					{
						_ref: "holder",
					},
					[
						el(
							"div.dhx_combobox-input__icon",
							{
								onclick: this._handlers.toggleIcon,
							},
							[
								el(
									"span" +
										(this.popup.isVisible() ? ".dxi.dxi-menu-up" : ".dxi.dxi-menu-down")
								),
							]
						),
						el(
							"div.dhx_combobox-input-list-wrapper",
							{
								onclick: this._handlers.oninputclick,
							},
							[
								el("ul.dhx_combobox-input-list", [
									...this._drawSelectedItems(), // selected items for multiselection mode
									el(
										"li.dhx_combobox-input-list__item.dhx_combobox-input-list__item--input",
										[
											el("input.dhx_combobox-input", {
												oninput: this._handlers.oninput,
												onfocus: this._handlers.onfocus,
												onblur: this._handlers.onblur,
												_ref: "input",
												_key: this._uid,
												type: "text",
												placeHolder:
													showPlaceholder && placeholder ? placeholder : undefined,
												value:
													readOnly && item
														? this._getItemText(item)
														: this._state.value,
												readOnly: readOnly || disabled,
												required,
												"aria-label": readOnly
													? "Select value"
													: "Type or select value",
												"aria-describedby": helpMessage
													? `dhx_label__help_${this._uid}`
													: null,
												"aria-expanded": true,
											}),
										]
									),
								]),
							]
						),
					]
				),
			]
		);
	}
	private _drawSelectedItems() {
		if (!this.config.multiselection) {
			return [];
		}
		if (this.config.itemsCount) {
			const count = (this.list.selection.getId() as Id[]).length;
			return count
				? [
						el("li.dhx_combobox-input-list__item.dhx_combobox-tag", [
							el(
								"span.dhx_combobox-tag__value",
								itemsCountTemplate(count, this.config.itemsCount)
							),
							el(
								"button.dhx_button.dhx_combobox-tag__action.dhx_combobox__action-clear-all",
								{ "aria-label": "clear all" },
								[el("span.dhx_button__icon.dxi.dxi-close-circle")]
							),
						]),
				  ]
				: [];
		}
		return (this.list.selection.getId() as string[]).map(id => {
			const item = this.data.getItem(id);
			if (!item) {
				return null;
			}
			return el("li.dhx_combobox-input-list__item.dhx_combobox-tag", { "data-dhx-id": id }, [
				this._drawImageOrIcon(item),
				el("span.dhx_combobox-tag__value", this._getItemText(item)),
				el(
					"button.dhx_button.dhx_button--icon.dhx_combobox-tag__action.dhx_combobox__action-remove",
					{
						type: "button",
						"aria-label": "remove",
					},
					[el("span.dhx_button__icon.dxi.dxi-close-circle")]
				),
			]);
		});
	}
	private _drawImageOrIcon(item: any) {
		if (item.src) {
			return el("img.dhx_combobox-tag__image", { src: item.src, alt: "" });
		} else if (item.icon) {
			return el("span.dhx_combobox-tag__icon", { class: item.icon });
		}
		return null;
	}
	private _getItemText(item: any) {
		if (!item) {
			return null;
		}
		return item.value;
	}
	private _updateSelectedItem(id: Id | Id[]) {
		if (this.config.multiselection) {
			if (
				this.config.selectAllButton &&
				!this._state.unselectActive &&
				this.data.getLength() === (id as Id[]).length
			) {
				this._layout.getCell("select-unselect-all").attach(unselectAllView);
				this._state.unselectActive = true;
			} else if (this.config.selectAllButton && this._state.unselectActive) {
				this._layout.getCell("select-unselect-all").attach(selectAllView);
				this._state.unselectActive = false;
			}

			if (this._state.value) {
				this._state.value = "";
				this._state.canDelete = (id as Id[]).length === 0;
				this._filter();
			}
		} else {
			this._state.value = this._getItemText(this.data.getItem(id as Id)) ?? "";
		}

		this.paint();
	}
	private _changePopupPosition() {
		if (this.config.multiselection) {
			awaitRedraw().then(() => {
				this._updatePopup();
			});
		}
	}
	private _updatePopup() {
		const holderNode: HTMLElement = this.getRootView().refs.holder.el;
		this.popup.getContainer().style.width = holderNode.offsetWidth + "px";

		const itemsHeight = this.data.getLength() * ((this.config.itemHeight as number) || 36);
		if (typeof this.config.listHeight === "string" && this.config.listHeight.includes("px")) {
			this.config.listHeight = this.config.listHeight.replace("px", "");
		}
		const listHeight = itemsHeight < this.config.listHeight ? itemsHeight : this.config.listHeight;
		this.popup.getContainer().style.height =
			Number(listHeight as any) +
			(this.config.selectAllButton &&
			this.config.multiselection &&
			this._layout.getCell("select-unselect-all").isVisible()
				? 33
				: 0) +
			(this._state.creatingState ? this._layout.getCell("not-found").height : 0) +
			"px";

		this.popup.show(holderNode, { mode: "bottom" });
	}
}
